'use client';

import React, { useState, useRef, useEffect } from 'react';
import { PresenceStatus, usePresence } from '@/hooks/usePresence';
import PresenceDot from '@/components/ui/PresenceDot';
import { ChevronDown } from 'lucide-react';

interface PresenceSelectorProps {
  userId: string;
  collapsed?: boolean;
}

const OPTIONS: { status: PresenceStatus; label: string }[] = [
  { status: 'online',       label: 'Online' },
  { status: 'away',         label: 'Away' },
  { status: 'in_a_meeting', label: 'In a meeting' },
  { status: 'offline',      label: 'Appear offline' },
];

export default function PresenceSelector({ userId, collapsed = false }: PresenceSelectorProps) {
  const { getStatus, setMyStatus } = usePresence(userId);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentStatus = getStatus(userId);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSelect(status: PresenceStatus) {
    await setMyStatus(status);
    setOpen(false);
  }

  if (collapsed) {
    return (
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center justify-center"
        title="Set status"
      >
        <PresenceDot status={currentStatus} size="md" />
        {open && (
          <div className="absolute left-6 bottom-0 z-50 bg-white border border-stone-200 rounded-xl shadow-lg py-1 w-44 overflow-hidden">
            {OPTIONS.map((opt) => (
              <button
                key={opt.status}
                onClick={() => handleSelect(opt.status)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12.5px] hover:bg-stone-50 transition-colors ${currentStatus === opt.status ? 'font-semibold text-slate-800' : 'text-slate-600'}`}
              >
                <PresenceDot status={opt.status} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
      >
        <PresenceDot status={currentStatus} />
        <span className="capitalize">{currentStatus.replace('_', ' ')}</span>
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-white border border-stone-200 rounded-xl shadow-lg py-1 w-44 overflow-hidden">
          {OPTIONS.map((opt) => (
            <button
              key={opt.status}
              onClick={() => handleSelect(opt.status)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12.5px] hover:bg-stone-50 transition-colors ${currentStatus === opt.status ? 'font-semibold text-slate-800' : 'text-slate-600'}`}
            >
              <PresenceDot status={opt.status} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
